import AutoSubmit from "@stimulus-components/auto-submit"

export default class extends AutoSubmit {
  static values = {
    delay: { type: Number, default: 700 },
    blurOnlyIfChanged: { type: Boolean, default: true },
    instantChange: { type: Boolean, default: true }
  }

  connect() {
    super.connect()

    // mark the change per field (for blur-only-if-changed)
    this._onInput = (e) => { e.target.dataset.changed = "true" }
    this.element.addEventListener("input", this._onInput, { passive: true })

    // state for queue
    this._isSubmitting = false
    this._queued = false

    // catch Turbo submit lifecycle on the form (works in turbo_frame)
    this._onSubmitStart = () => {
      this._isSubmitting = true
      const form = this.element.matches("form") ? this.element : this.element.closest("form")
      if (form) form.dataset.submitting = "true"
    }

    this._onSubmitEnd = () => {
      this._isSubmitting = false

      // Clear all data-changed flags after submit
      const form = this.element.matches("form")
        ? this.element
        : this.element.closest("form")

      if (form) {
        form.dataset.submitting = "false"
        form.querySelectorAll('[data-changed="true"]').forEach(field => {
          field.dataset.changed = "false"
        })
      }

      if (this._queued) {
        this._queued = false
        this._instantSubmit()
      }

      // Note: autosave:success is dispatched by Turbo Stream action (autosave_success.js)
      // The server handles it via turbo_stream.action(:autosave_success)
    }

    this.element.addEventListener("turbo:submit-start", this._onSubmitStart)
    this.element.addEventListener("turbo:submit-end", this._onSubmitEnd)

    // Submit form when clicking on links/navigation before navigation occurs
    this._onGlobalClick = (event) => {
      if (this.#clickInsideAddStepPicker(event)) return

      const form = this.element.matches("form")
        ? this.element
        : this.element.closest("form")
      if (!form) return

      // Check if click is inside the form (don't submit if clicking form elements)
      if (form.contains(event.target)) return

      // Check if clicking on a link or navigation element
      const link = event.target.closest('a[href], button[type="submit"], [data-turbo-frame]')
      if (!link) return

      // Ignore if link has auto-submit-ignore attribute
      if (link.dataset.autoSubmitIgnore === 'true') return

      // Check if any field has changed
      const changedField = form.querySelector('[data-changed="true"]')
      if (changedField && !this._isSubmitting) {
        // Submit the form immediately
        this._instantSubmit()
      }
    }
    document.addEventListener("click", this._onGlobalClick, true)

    // Allow other UI actions (e.g. publish/tab switch) to request a flush.
    this._onAutosaveFlush = () => {
      const form = this.element.matches("form")
        ? this.element
        : this.element.closest("form")
      if (!form || this._isSubmitting) return

      const changedField = form.querySelector('[data-changed="true"]')
      const activeInForm =
        document.activeElement &&
        document.activeElement !== document.body &&
        form.contains(document.activeElement)

      if (changedField || activeInForm) {
        this._instantSubmit()
      }
    }
    document.addEventListener("autosave:flush", this._onAutosaveFlush)
  }

  disconnect() {
    this.element.removeEventListener("input", this._onInput)
    this.element.removeEventListener("turbo:submit-start", this._onSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    document.removeEventListener("click", this._onGlobalClick, true)
    document.removeEventListener("autosave:flush", this._onAutosaveFlush)
    super.disconnect()
  }

  submit(event) {
    if (event?.target?.dataset.autoSubmitIgnore === "true") {
      return
    }

    // Reset field: always direct submit
    if (event?.target?.dataset.fieldReset === "true") {
      return this._instantSubmit()
    }

    // If a submit is in progress: mark that we have a pending change
    if (this._isSubmitting) {
      this._queued = true
      if (event?.target) event.target.dataset.changed = "true"
      return
    }

    // change: go via debounce instead of instant
    if (this.instantChangeValue && event?.type === "change") {
      if (event?.target) event.target.dataset.changed = "true"
      return super.submit(event) // use delayValue
    }

    // blur: submit only if the field has actually changed
    if (this.blurOnlyIfChangedValue && event?.type === "blur") {
      const el = event.target
      if (el && el.dataset.changed !== "true") return
      if (el) el.dataset.changed = "false"
    }

    // input/enter: standard debounce from AutoSubmit
    return super.submit(event)
  }

  _instantSubmit() {
    const form = this.element.matches("form")
      ? this.element
      : this.element.closest("form")
    if (!form) return

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  #clickInsideAddStepPicker(event) {
    if (event.target.closest("turbo-frame#add_step_modal_frame")) return true

    const dialog = event.target.closest("dialog")
    return Boolean(dialog?.querySelector("turbo-frame#add_step_modal_frame"))
  }
}
